// Resolve the on-chain token identity of a property's primary offering.
//
// Published for the wallets module (architecture-principles §10 — shared-endpoint
// hooks). Wallet management has to open XRPL trustlines for properties an
// investor has ordered into, and a trustline needs the offering id plus the
// token's issuer address and currency code. An order row carries none of that,
// and the wallet-management payload only carries it for orders the VAISP has
// already parked — so without this the screen can only say "no token data
// recorded" and stop.
//
// Two hops, because no single endpoint answers it: property → its offerings →
// that offering's Ctrl-Alt listing. One query per property so each result caches
// and dedupes on its own, rather than one coarse key that refetches everything
// whenever the property set changes.

import { useQueries } from '@tanstack/vue-query'
import { computed, toValue, type MaybeRefOrGetter } from 'vue'

import { getOfferingVaspListing, getPropertyOfferings } from '../offeringPropertyEndpoints'
import { offeringPropertyQueryKeys } from '../offeringPropertyQueryKeys'
import { OFFERING_TYPE } from '../../constants/offeringStatus'

/**
 * What one property's primary offering says about its token.
 *
 * Every field is independently absent-able, and that is the point: an offering
 * that exists but was never sent to Ctrl-Alt resolves `offeringId` and nothing
 * else, which is strictly more than the consumer knew before and lets it name
 * the remaining gap precisely.
 */
export interface PropertyTokenIdentity {
  propertyId: string
  offeringId: string
  offeringTitle: string
  /** Ctrl-Alt's name for the token. Empty until the listing is created. */
  tokenName: string
  /** 40-character XRPL currency code. Empty until the listing is created. */
  tokenHex: string
  /** The Ctrl-Alt issuer wallet a trustline is opened against. */
  issuerAddress: string
}

const EMPTY_IDENTITY = (propertyId: string): PropertyTokenIdentity => ({
  propertyId,
  offeringId: '',
  offeringTitle: '',
  tokenName: '',
  tokenHex: '',
  issuerAddress: '',
})

/**
 * A property carries exactly one primary-market offering, so the type is the
 * whole selection rule. Falling back to the sole offering covers rows the
 * backend returns without a type set rather than resolving nothing for them.
 */
function selectPrimaryOffering<
  T extends { id: string; offeringType: string; offeringTitle: string },
>(offerings: T[]): T | undefined {
  const primary = offerings.find((offering) => offering.offeringType === OFFERING_TYPE.PRIMARY)
  if (primary) return primary
  return offerings.length === 1 ? offerings[0] : undefined
}

async function getPropertyTokenIdentity(propertyId: string): Promise<PropertyTokenIdentity> {
  const offerings = await getPropertyOfferings(propertyId)
  const offering = selectPrimaryOffering(offerings)
  if (!offering) return EMPTY_IDENTITY(propertyId)

  const resolved: PropertyTokenIdentity = {
    ...EMPTY_IDENTITY(propertyId),
    offeringId: offering.id,
    offeringTitle: offering.offeringTitle,
  }

  // An offering never sent to Ctrl-Alt answers 409 VASP_LISTING_NOT_CREATED
  // (not a 404, despite how it reads). That is a normal state, not a failure:
  // the offering is real and the token simply does not exist yet. Swallowing it
  // keeps the offering half of the answer, which is what makes the consumer's
  // warning specific instead of blanket.
  try {
    const listing = await getOfferingVaspListing(offering.id)
    return {
      ...resolved,
      tokenName: listing.tokenName ?? '',
      tokenHex: listing.tokenHex ?? '',
      issuerAddress: listing.issuerAddress ?? '',
    }
  } catch {
    return resolved
  }
}

// Two requests per property, so the fan-out is bounded. A wallet-management
// detail screen showing more gaps than this has an operational problem the list
// itself surfaces; it should not also fire 100 request pairs at admin-service.
export const PROPERTY_TOKEN_IDENTITY_MAX_LOOKUPS = 25

/**
 * Token identity for each property id, keyed by property id.
 *
 * Pass only the ids actually missing an identity — every id costs two requests,
 * and the ones the caller already knows are the common case.
 */
export function usePropertyTokenIdentitiesQuery(propertyIds: MaybeRefOrGetter<string[]>) {
  const ids = computed(() =>
    [...new Set(toValue(propertyIds).filter(Boolean))].slice(
      0,
      PROPERTY_TOKEN_IDENTITY_MAX_LOOKUPS,
    ),
  )

  const results = useQueries({
    queries: computed(() =>
      ids.value.map((propertyId) => ({
        queryKey: offeringPropertyQueryKeys.tokenIdentity(propertyId),
        queryFn: () => getPropertyTokenIdentity(propertyId),
        // The pair behind this rarely changes once a listing exists, and the
        // screen re-derives on every gap recomputation.
        staleTime: 1000 * 60 * 5,
        retry: false,
      })),
    ),
  })

  const identitiesByPropertyId = computed(() => {
    const map = new Map<string, PropertyTokenIdentity>()
    for (const result of results.value) {
      if (result.data) map.set(result.data.propertyId, result.data)
    }
    return map
  })

  const isLoading = computed(() => results.value.some((result) => result.isLoading))

  /** True when the caller passed more ids than the fan-out cap allows. */
  const isCapped = computed(
    () =>
      new Set(toValue(propertyIds).filter(Boolean)).size >
      PROPERTY_TOKEN_IDENTITY_MAX_LOOKUPS,
  )

  return { identitiesByPropertyId, isLoading, isCapped }
}
